import { capitalizeWords } from './capitalize-words';

/**
 * Live-agent platform identity, as Pro Studio has to read it.
 *
 * The backend catalog (see `get-agent-routing-schemas`) returns every
 * integration that exists on the platform, in two families that overlap by
 * name:
 *
 * - the LAAPI middleware connectors, whose integration names carry a
 *   `LAAPI_` prefix (`LAAPI_Quiq`, `LAAPI_Zendesk`, …) — these are what
 *   Pro Studio supports, together with the Contact Center connector;
 * - the older prime-app native routings (`quiq`, `salesforce`, `nuance`,
 *   `freshchat`, …), which Pro Studio does not support.
 *
 * PSTUDIO-4072 asked for two things at once, and they are the same rule seen
 * from two sides: only Contact Center + LAAPI integrations may be OFFERED, and
 * a LAAPI integration must be shown under the platform name alone. Stripping
 * the prefix is only safe BECAUSE the legacy twins are not offered alongside —
 * e.g. `LAAPI_Quiq` and the native `quiq` would otherwise both read "Quiq".
 *
 * The prefix is the rule, deliberately not an allowlist of names: a LAAPI
 * integration added on the platform side later shows up without a code change,
 * which is the whole reason the catalog is fetched instead of hardcoded.
 *
 * The integration name is ALSO the platform's display name (there is no
 * separate display-name field), and it is what Pro Studio persists as
 * `PypeConnection.platformId`. So this module only ever changes what is
 * RENDERED — never what is stored or sent.
 */
export const LAAPI_PLATFORM_PREFIX = 'LAAPI_';

/**
 * Integration names are typed by hand on the platform side, so every CATALOG
 * predicate reads a trimmed value: a stray leading space would otherwise drop
 * the platform out of the picker with no signal in the UI or the console.
 *
 * Deliberately not applied to `isContactCenterPlatform` below — see the note
 * there. Tightening a shared predicate for one caller's sake silently moves the
 * other caller's contract (memory `feedback_shared_predicate_two_contracts`).
 */
const normalizeName = (name?: string | null): string => (name ?? '').trim();

/**
 * Routing values a PYPE can carry for a Contact Center connector — the key
 * space of the `agent_routing` enum, which is what Default Messages asks
 * `isContactCenterPlatform` about.
 *
 * Deliberately SEPARATE from `CONTACT_CENTER_INTEGRATION_NAMES` below even
 * though the spellings overlap today: one set gates a banner, the other gates
 * the catalog, and a change made for one must not silently move the other
 * (see `feedback_shared_predicate_two_contracts`).
 */
const CONTACT_CENTER_AGENT_ROUTINGS = new Set([
  'cc',
  'contact_center',
  'contactcenter',
]);

/**
 * The Contact Center connector's integration name, as the catalog reports it and
 * as a channel's config persists it. Declared HERE rather than imported from
 * `mutate-channel` (which re-exports it): that module pulls in `api-services`,
 * and this one is imported by `platform-logo`, whose whole point is to stay free
 * of heavy module-load work (commit `7e129afeb7`).
 */
export const CONTACT_CENTER_INTEGRATION_NAME = 'ContactCenter';

/** The connector as a user reads it. Display only — never persisted or sent. */
const CONTACT_CENTER_DISPLAY_NAME = 'Contact Center';

const CONTACT_CENTER_INTEGRATION_NAME_FOLDED =
  CONTACT_CENTER_INTEGRATION_NAME.toLowerCase();

/**
 * Spellings of the Contact Center connector as an INTEGRATION NAME, i.e. as the
 * catalog reports it. `ContactCenter` is what external integrations use; the
 * other two come from internal schemas, whose `agent_routing` doubles as their
 * catalog key.
 */
const CONTACT_CENTER_INTEGRATION_NAMES = new Set([
  CONTACT_CENTER_INTEGRATION_NAME_FOLDED,
  'cc',
  'contact_center',
]);

/**
 * Contact Center connectors manage their auto-response copy at the inbox level,
 * not here.
 *
 * Reads the value as stored, WITHOUT the catalog's `normalizeName` trim. The
 * trim was added for hand-typed integration names, and this predicate answers a
 * different question about a different field (a pype's `agent_routing`): folding
 * `" cc "` to Contact Center here would newly HIDE the busy/offline/technical
 * copy for such a pype on Default Messages — a behaviour change with no ticket
 * behind it. Two contracts, two normalizations (`feedback_shared_predicate_two_contracts`).
 */
export const isContactCenterPlatform = (
  agentRouting?: string | null
): boolean =>
  CONTACT_CENTER_AGENT_ROUTINGS.has((agentRouting ?? '').toLowerCase());

/**
 * A real prefix, not a substring: `NotLAAPI_Quiq` is not a LAAPI integration.
 * The bare prefix names no platform either, so it doesn't qualify.
 */
const hasLaapiPrefix = (name: string): boolean =>
  name.length > LAAPI_PLATFORM_PREFIX.length &&
  name.toLowerCase().startsWith(LAAPI_PLATFORM_PREFIX.toLowerCase());

/**
 * Whether Pro Studio OFFERS this platform when creating a connection.
 *
 * Scope note: this gates what the picker RENDERS, never the catalog held in
 * state and never the list of existing connections. The full catalog is kept
 * precisely so an existing connection on an unsupported platform still resolves
 * to its schema — that is what keeps its credential fields editable (and what
 * keeps the submit's "trust the stored routing" guard meaning what it says).
 */
export const isSupportedPlatform = (name?: string | null): boolean => {
  const value = normalizeName(name);
  return (
    CONTACT_CENTER_INTEGRATION_NAMES.has(value.toLowerCase()) ||
    hasLaapiPrefix(value)
  );
};

/**
 * Fold a platform key for COMPARISON only — never for storage.
 *
 * `ps-select-option` rewrites a space in its `value` to `_` (`select-option.wc.ts`'s
 * `@watch`), so the form can hand back a key the catalog does not contain. Every
 * place that answers "is this the same platform?" has to fold both sides, or the
 * answer flips on a platform whose integration name contains a space: the schema
 * resolver folds (it always did), and so does the payload's `platformUnchanged` —
 * which, ungated, made merely RE-PICKING the same row read as a platform switch
 * and drop the connection's stored config and callback URL.
 *
 * Comparison only: `platformId` and `integration_name` stay RAW, because the
 * legacy Console matches on the value the backend actually holds.
 */
export const foldPlatformKey = (name?: string | null): string =>
  normalizeName(name).replace(/[\s_]+/g, '_');

/** `LAAPI_Quiq` -> `Quiq`. Anything without the prefix is returned unchanged. */
export const stripLaapiPrefix = (name?: string | null): string => {
  const value = normalizeName(name);
  return hasLaapiPrefix(value)
    ? value.slice(LAAPI_PLATFORM_PREFIX.length)
    : value;
};

/**
 * Read a CamelCase integration name as words: `AmazonConnect` -> `Amazon
 * Connect`.
 *
 * A run of capitals is ONE word, not one word per letter — the LAAPI family
 * holds `GoogleCCAI` and `ZendeskVoiceAI`, and splitting on every capital would
 * print "Google C C A I". Hence two rules rather than one:
 *
 * - a break after a lowercase letter or digit that is followed by a capital
 *   (`Amazon|Connect`, `S3|Connector`);
 * - inside a run of capitals, a break before the LAST one only when a lowercase
 *   letter follows it, since that capital starts the next word
 *   (`Zendesk Voice|AI` stays whole because nothing follows).
 *
 * Unicode property escapes rather than `[a-z]`/`[A-Z]`: an integration name is
 * hand-typed, and an accented initial would otherwise silently skip the break.
 *
 * Applied ONLY to a name that carried the `LAAPI_` prefix — see the caller.
 */
const splitCamelCase = (value: string): string =>
  value
    .replace(/(\p{Ll}|\p{N})(\p{Lu})/gu, '$1 $2')
    .replace(/(\p{Lu}+)(\p{Lu}\p{Ll})/gu, '$1 $2');

/**
 * The platform name as a user should read it: prefix stripped, `_`/`-` read as
 * word breaks, and capitalized so a legacy lowercase routing value (`quiq`)
 * still renders as a name. Applies to every surface that shows a platform — the
 * picker and the table tooltip.
 *
 * The CamelCase split is what PSTUDIO-4072's review asked for ("renamed to
 * remove the prefix and have spaces where appropriate"): the names the LAAPI
 * family actually holds are `LAAPI_AmazonConnect` / `LAAPI_SalesforceMessaging`,
 * so stripping the prefix alone still read "AmazonConnect" — the very thing the
 * ticket set out to fix, one layer down.
 *
 * It is deliberately scoped to names that CARRIED THE PREFIX, and that scope is
 * the whole correctness argument. A CamelCase hump is not a word boundary in
 * general — it is one in a machine-generated `LAAPI_*` key, and it is a BRAND
 * everywhere else. Applied to the whole catalog the same rule prints
 * `ServiceNow` as "Service Now" and `LivePerson` as "Live Person" (both are
 * live in production, and both render in the table tooltip and in a pinned
 * "(Legacy)" row), plus `NICEinContact` as "NIC Ein Contact" — i.e. it would
 * fix one half of the catalog by breaking the other.
 *
 * The one name read as words WITHOUT the prefix is the Contact Center
 * connector, matched by NAME rather than by rule: it is a Pypestream product
 * ("Contact Center"), not a vendor's spelling, and the picker printing it as
 * `ContactCenter` was a leak of the integration key. The cost is a catalog
 * that holds both this integration and the internal `cc` schema (whose own
 * label already reads "Contact Center"): `disambiguateLabels` then appends each
 * raw key, which is what keeps the two rows apart.
 *
 * Display only, as everywhere in this module: `agent_routing` / `platformId`
 * stay RAW, so nothing here reaches the wire.
 */
export const getPlatformDisplayName = (name?: string | null): string => {
  const value = normalizeName(name);
  // Compared against the ONE spelling, not `CONTACT_CENTER_INTEGRATION_NAMES`:
  // that set also holds `cc` / `contact_center`, which keep their own labels.
  if (value.toLowerCase() === CONTACT_CENTER_INTEGRATION_NAME_FOLDED) {
    return CONTACT_CENTER_DISPLAY_NAME;
  }

  const spaced = stripLaapiPrefix(value).replace(/[_-]+/g, ' ');

  return capitalizeWords(
    (hasLaapiPrefix(value) ? splitCamelCase(spaced) : spaced)
      .replace(/\s+/g, ' ')
      .trim()
  );
};

/**
 * One collator for every platform list, built once.
 *
 * The locale is PINNED rather than left to the environment. The app sorts in the
 * browser while the e2e computes its expectation in Node, so two independent
 * `undefined` locales would disagree on a non-ASCII initial (`Å` sorts after
 * `Z` in sv-SE) and redden a correct implementation on such a machine.
 * `sensitivity: 'base'` makes a case-only difference compare EQUAL, which the
 * stable sort then leaves in backend order.
 *
 * Building it once also matters: `String.prototype.localeCompare` with a
 * non-empty options object constructs a collator per comparison (measured ~30x
 * slower on a 30-item sort).
 */
export const PLATFORM_LABEL_COLLATOR = new Intl.Collator('en', {
  sensitivity: 'base',
});

/**
 * Order a platform list A-Z by the name the user actually reads.
 *
 * On the LABEL, deliberately not on the raw name: every LAAPI integration
 * shares the same prefix, so sorting on `agent_routing` would clump all of them
 * together under "L" while the picker shows them under their own initials — the
 * list would look unsorted.
 *
 * Returns a new array; the caller's list is never mutated (the catalog is held
 * in XState context, and `Array.prototype.sort` sorts in place).
 */
export const sortPlatformsByLabel = <T extends { label: string }>(
  platforms: readonly T[]
): T[] =>
  [...platforms].sort((a, b) =>
    PLATFORM_LABEL_COLLATOR.compare(a.label, b.label)
  );
